import { ArrowDown, ArrowUp, ChevronsUpDown, Info } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { useCallback } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SortableHeaderProps {
  children: React.ReactNode;
  column: string;
  currentSort: string | null;
  currentOrder: "ASC" | "DESC";
  onSortChange: (column: string | null, order: "ASC" | "DESC") => void;
  tooltip?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties; // 1. Adicionado para receber a largura dinâmica
}

export function SortableHeader({
  children,
  column,
  currentSort,
  currentOrder,
  onSortChange,
  className,
  tooltip,
  style, // 1. Recebendo o style
}: Readonly<SortableHeaderProps>) {
  const isActive = currentSort === column;

  const handleClick = useCallback(() => {
    if (currentSort !== column) {
      onSortChange(column, "ASC");
    } else if (currentOrder === "ASC") {
      onSortChange(column, "DESC");
    } else {
      onSortChange(null, "ASC");
    }
  }, [currentSort, currentOrder, column, onSortChange]);

  const ariaSort = !isActive ? "none" : currentOrder === "ASC" ? "ascending" : "descending";

  return (
    <TableHead
      aria-sort={ariaSort}
      style={style} // 1. Aplicando a largura (width) aqui
      className={cn(
        // 2. Adicionado 'relative' para ancorar o ResizeHandle
        "group relative cursor-pointer select-none whitespace-nowrap transition-colors hover:bg-muted/50",
        className,
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2">
        {/* Texto da Coluna */}
        {/* 3. Mudado de 'span' para 'div' para aceitar o ResizeHandle (que é div) validamente */}
        <div
          className={cn(
            "flex-1 truncate font-medium transition-colors", // truncate ajuda se a coluna ficar muito pequena
            isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground",
          )}
        >
          {children}
        </div>

        {/* Ícones de Ordenação */}
        <span className="flex shrink-0 items-center justify-center">
          {" "}
          {/* shrink-0 evita esmagar o ícone */}
          {isActive ? (
            currentOrder === "ASC" ? (
              <ArrowUp className="h-3.5 w-3.5 text-primary duration-200 animate-in fade-in zoom-in" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 text-primary duration-200 animate-in fade-in zoom-in" />
            )
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50 opacity-0 transition-all group-hover:opacity-100" />
          )}
        </span>

        {/* Tooltip Informativo */}
        {tooltip && (
          <div onClick={(e) => e.stopPropagation()} className="ml-1 flex shrink-0 items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="cursor-help rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                    <Info className="h-3.5 w-3.5" />
                    <span className="sr-only">Informação sobre {children}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[250px] font-normal">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
    </TableHead>
  );
}
